/**
 * Shadow-DOM CSS for the DevPill. Extracted from dev-pill.tsx as part of
 * Phase 6 of the round-3 enactment plan (review-9016e0cc-83b #7).
 *
 * Imported by dev-pill.tsx and injected into the shadow root in
 * DevPillImpl's mount effect. Do not consume from outside dev-pill.tsx —
 * grep for `import .* STYLES` to verify before exporting.
 */
export const STYLES = `
:host {
  all: initial;
  position: fixed !important;
  display: flex !important;
  z-index: 2147483647 !important;
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 14px;
  line-height: 1;
  color: #999;
  pointer-events: none;
}

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

button {
  all: unset;
  font: inherit;
  color: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.pill {
  position: relative;
  display: flex;
  align-items: center;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 23px;
  padding: 2px;
  gap: 5px;
  box-shadow: none;
  user-select: none;
  pointer-events: auto;
  touch-action: none;
}

.section {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 18px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
  color: #888;
}

.section:hover { background: #2a2a2a; color: #ccc; }
.section.active { color: #fff; }

.divider {
  width: 1px;
  height: 18px;
  background: #333;
  flex-shrink: 0;
}

/* Worktree popup */

.section-wrap { position: relative; }
.section-wrap.worktree-wrap { position: static; }

.wt-popup {
  position: absolute;
  bottom: calc(100% + 14px);
  right: 0;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 9px;
  padding: 5px;
  min-width: 160px;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.5);
  pointer-events: auto;
}

.pill-container[data-popup-down='true'] .wt-popup,
.pill-container[data-popup-down='true'] .menu-popup {
  bottom: auto;
  top: calc(100% + 14px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

.pill-container[data-popup-right-anchor='true'] .wt-popup,
.pill-container[data-popup-right-anchor='true'] .menu-popup {
  right: auto;
  left: 0;
}

.wt-item {
  padding: 7px 14px;
  border-radius: 5px;
  cursor: pointer;
  white-space: nowrap;
  color: #999;
  font-size: 14px;
  transition: background 0.1s, color 0.1s;
  display: flex;
  justify-content: space-between;
  gap: 14px;
}

.wt-item:hover { background: #2a2a2a; color: #ddd; }
.wt-item.current { color: #7c7; }
.wt-item.disabled { color: #555; cursor: default; }
.wt-item.disabled:hover { background: transparent; color: #555; }
.wt-port { color: #666; font-size: 13px; }

/* Custom section popup */

.menu-popup {
  position: absolute;
  bottom: calc(100% + 14px);
  right: 0;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 9px;
  padding: 5px;
  min-width: 230px;
  max-width: calc(100vw - 16px);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.5);
  pointer-events: auto;
  max-height: 70vh;
  overflow-y: auto;
}

@media (max-width: 639px) {
  .menu-popup {
    position: fixed;
    bottom: 60px;
    left: 8px;
    right: 8px;
    transform: none;
    min-width: 0;
  }
  .pill-container[data-popup-down='true'] .menu-popup {
    bottom: auto;
    top: 60px;
  }
  /* Mobile insets win over the right-anchor desktop override — without this
     rule, [data-popup-right-anchor] would null out the right: 8px inset and
     the popup would shrink to its min-width when the pill is on the left edge. */
  .pill-container[data-popup-right-anchor='true'] .menu-popup {
    left: 8px;
    right: 8px;
  }
}

.menu-item {
  padding: 7px 14px;
  border-radius: 5px;
  cursor: pointer;
  white-space: nowrap;
  color: #999;
  font-size: 14px;
  transition: background 0.1s, color 0.1s;
  display: flex;
  align-items: center;
  gap: 9px;
  text-decoration: none;
  border: none;
  background: none;
  font: inherit;
  width: 100%;
  text-align: left;
}

.menu-item:hover { background: #2a2a2a; color: #ddd; }
.menu-item.active { color: #fff; }
.menu-item.disabled { color: #555; cursor: default; pointer-events: none; }
.menu-item.disabled:hover { background: transparent; color: #555; }
.menu-item.accent-purple { color: #c084fc; }
.menu-item.accent-purple:hover { color: #d8b4fe; }
.menu-item.accent-amber { color: #fbbf24; }
.menu-item.accent-amber:hover { color: #fcd34d; }

.menu-separator { height: 1px; background: #333; margin: 5px 0; }

/* Cascading submenu — flyout panel to the left of the trigger row. The menu
   sits at the bottom-right of the viewport, so flyouts cascade leftward to
   stay onscreen. */
.menu-group-cascade { position: relative; }
.menu-group-cascade-panel {
  position: absolute;
  right: calc(100% + 5px);
  top: 0;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 9px;
  padding: 5px;
  min-width: 230px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  z-index: 1;
}

/* When the parent menu-popup is right-anchored (pill docked to left edge),
   the cascade panel must open to the right of the trigger instead of the
   left — otherwise the submenu lands off the left edge of the viewport. */
.pill-container[data-popup-right-anchor='true'] .menu-popup .menu-group-cascade-panel {
  right: auto;
  left: calc(100% + 5px);
}

@media (max-width: 639px) {
  /* On narrow viewports the parent menu-popup is already pinned to the screen
     edges; flyouts cascade downward beneath the trigger instead so they don't
     get clipped. */
  .menu-group-cascade-panel {
    position: static;
    margin-top: 4px;
    box-shadow: none;
    border: none;
    padding: 0 0 0 8px;
    min-width: 0;
  }
}

/* Next.js section */

.nextjs { padding: 6px 9px; }
.nextjs-ok { background: #000; opacity: 0.6; }
.nextjs-ok:hover { opacity: 0.8; }
.nextjs-err { background: #b91c1c; opacity: 1; }

/* React scan stats */

.scan-stat { color: #666; font-size: 13px; display: flex; align-items: center; gap: 4px; }
.scan-value { color: #777; }

/* React scan disabled (mobile) */

.scan-off {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.scan-off::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 21px;
  height: 2px;
  background: currentColor;
  transform: translate(-50%, -50%) rotate(-45deg);
  border-radius: 1px;
  pointer-events: none;
}

svg { display: block; flex-shrink: 0; }

/* Mobile collapse/expand */

.pill-container {
  display: flex;
  align-items: center;
}

.pill-fab {
  display: flex;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #1a1a1a;
  border: 1px solid #333;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  cursor: pointer;
  color: #888;
  transition: color 0.15s;
  touch-action: none;
}
.pill-fab:hover { color: #ccc; }

.pill-collapse {
  display: inline-flex;
  padding: 6px 7px;
  border-radius: 18px;
  cursor: pointer;
  color: #666;
  transition: color 0.15s;
  align-items: center;
}
.pill-collapse:hover { color: #ccc; }

/* Collapse/expand is class-driven at all breakpoints */
.pill-container.expanded .pill-fab { display: none; }
.pill-container.expanded .pill { display: flex; }
.pill-container:not(.expanded) .pill { display: none; }
.pill-container:not(.expanded) .pill-collapse { display: none; }
`;
